import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { appPrompt, appConfirm } from "@/components/AppDialogHost";

const STATUS_CLS = {
  published: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  draft: "bg-bg-elevated text-ink-secondary border-line",
  review: "bg-brand-cyan/15 text-brand-cyan border-brand-cyan/40",
  scheduled: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  unpublished: "bg-red-500/10 text-red-300 border-red-500/40",
};

export default function MagArticles() {
  const [articles, setArticles] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [pubSlug, setPubSlug] = useState("");
  const nav = useNavigate();

  const load = useCallback(async (s, query) => {
    const r = await api.get("/mag/articles", { params: { status: s, q: query } });
    setArticles(r.data.articles || []);
  }, []);
  useEffect(() => { load(status, q); }, [status, q, load]);
  useEffect(() => { api.get("/mag/publication").then((r) => setPubSlug(r.data.slug)); }, []);

  const action = async (id, act, extra = {}) => {
    try {
      const r = await api.post(`/mag/articles/${id}/action`, { action: act, ...extra });
      toast.success(act === "duplicate" ? "Article duplicated" : `Article ${act}${act.endsWith("e") ? "d" : "ed"}`);
      if (act === "duplicate" && r.data.article_id) nav(`/dashboard/magazine/editor/${r.data.article_id}`);
      else load(status, q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const schedule = async (id) => {
    const when = await appPrompt("Publish date & time (YYYY-MM-DD HH:MM, UTC):");
    if (!when) return;
    const iso = new Date(when.replace(" ", "T") + "Z").toISOString();
    action(id, "schedule", { scheduled_at: iso });
  };
  const del = async (id) => { if (await appConfirm("Delete this article permanently?")) api.delete(`/mag/articles/${id}`).then(() => load(status, q)); };

  return (
    <div className="space-y-4" data-testid="mag-articles">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold">Articles</h1>
        <div className="flex gap-2">
          <Link to="/dashboard/magazine/editor/new" data-testid="mag-new-article-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-2 text-sm font-bold"><Plus className="h-4 w-4" /> Create Article</Link>
          <Link to="/dashboard/magazine/ai-writer" className="inline-flex items-center gap-1.5 rounded-full border border-brand-cyan/60 text-brand-cyan px-4 py-2 text-sm font-bold"><Sparkles className="h-4 w-4" /> AI Writer</Link>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles…" data-testid="mag-articles-search"
          className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm min-w-[200px] focus:border-brand-amber focus:outline-none" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="mag-status-filter"
          className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {["draft", "review", "scheduled", "published", "unpublished"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line">
        {articles.map((a) => (
          <div key={a.article_id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`mag-article-row-${a.article_id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold">{a.title} {a.is_featured && <span className="text-brand-amber">★</span>}</div>
              <div className="text-[11px] font-mono text-ink-muted">{a.views} views · updated {(a.updated_at || "").slice(0, 10)}{a.status === "scheduled" && a.scheduled_at ? ` · goes live ${a.scheduled_at.slice(0, 16).replace("T", " ")} UTC` : ""}</div>
            </div>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_CLS[a.status]}`}>{a.status}</span>
            <div className="flex gap-1 text-xs">
              <Link to={`/dashboard/magazine/editor/${a.article_id}`} className="px-2 py-1 rounded border border-line hover:border-brand-amber/60" data-testid={`mag-edit-${a.article_id}`}>Edit</Link>
              {a.status === "published" ? (
                <>
                  <a href={`/mag/${pubSlug}/a/${a.slug}`} target="_blank" rel="noreferrer" className="px-2 py-1 rounded border border-line hover:border-brand-cyan/60">View</a>
                  <button onClick={() => action(a.article_id, "unpublish")} className="px-2 py-1 rounded border border-line hover:border-red-400/60" data-testid={`mag-unpublish-${a.article_id}`}>Unpublish</button>
                </>
              ) : (
                <>
                  <button onClick={() => action(a.article_id, "publish")} className="px-2 py-1 rounded border border-emerald-500/50 text-emerald-300" data-testid={`mag-publish-${a.article_id}`}>Publish</button>
                  <button onClick={() => schedule(a.article_id)} className="px-2 py-1 rounded border border-line hover:border-amber-400/60" data-testid={`mag-schedule-${a.article_id}`}>Schedule</button>
                </>
              )}
              <button onClick={() => action(a.article_id, "duplicate")} className="px-2 py-1 rounded border border-line" data-testid={`mag-duplicate-${a.article_id}`}>Duplicate</button>
              <button onClick={() => del(a.article_id)} className="px-2 py-1 rounded border border-line text-red-300/80" data-testid={`mag-delete-${a.article_id}`}>Delete</button>
            </div>
          </div>
        ))}
        {!articles.length && <div className="p-8 text-center text-sm text-ink-muted">No articles yet — create one or use the AI Writer.</div>}
      </div>
    </div>
  );
}
